import { JetView } from 'webix-jet'; // Importing JetView from the webix-jet library for creating views
import {
    devConfig, // Development configuration settings
    countries, // List of countries for filtering
    profile, // User profile information
    cashDesks, // Cash desk information
    agentList, // List of agents
} from 'config/config'; // Importing configuration data from the config module

/**
 * Filter class extends JetView to create a specific view for filtering data.
 */
export default class Filter extends JetView {
    /**
     * Configures the view.
     * @returns {Object} Configuration object for the view.
     */
    config() {
        // Retrieve the localization service from the app
        const _ = this.app.getService('locale')._;
        // Define the layout for the left section of the UI
        const left = {
            margin: 5, // Set margin for the left section
            id: 'a1', // Unique identifier for the left section
            rows: [
                // Define rows within the left section
                {
                    margin: 15, // Margin for the row
                    css: 'on-row', // CSS class for styling
                    responsive: 'a1', // Responsive design identifier
                    cols: [
                        // Define columns within the row
                        {
                            // Column for filtering by user, visibility based on permissions
                            hidden: devConfig.getPermAction('filterByUser', 'Add'),
                            css: 'prm-filterByUser', // CSS class for styling
                            height: 1, // Set height for the column
                        },
                        {
                            // Input field for User ID
                            view: 'text', // Type of input
                            name: 'UserId', // Name of the input field
                            type: 'number', // Input type
                            label: "<span class='icon-drivers-license-o'></span>" + _('ID Usuario'), // Label with icon
                            hidden: !devConfig.getPermAction('filterByUser', 'Add'), // Visibility based on permissions
                            css: 'prm-filterByUser', // CSS class for styling
                            labelPosition: 'top', // Position of the label
                            placeholder: _('ID Usuario'), // Placeholder text
                        },
                        {
                            // Input field for Name
                            view: 'text', // Type of input
                            name: 'Name', // Name of the input field
                            label: "<span class='icon-user'></span>" + _('Nombre'), // Label with icon
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Nombre'), // Placeholder text
                            hidden: !devConfig.getPermAction('filterByUser', 'Add'), // Visibility based on permissions
                            css: 'prm-filterByUser', // CSS class for styling
                        },
                        {
                            // Input field for Document number
                            view: 'text', // Type of input
                            name: 'Document', // Name of the input field
                            minWidth: 200, // Minimum width for the column
                            label: _('Número cédula'), // Label text
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Numero cédula'), // Placeholder text
                        },
                        {
                            // Input field for Login
                            view: 'text', // Type of input
                            name: 'Login', // Name of the input field
                            label: _('Login'), // Label text
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Login'), // Placeholder text
                            hidden: !devConfig.getPermAction('filterByUser', 'Add'), // Visibility based on permissions
                            css: 'prm-filterByUser', // CSS class for styling
                        },
                    ],
                },
                {
                    margin: 15, // Margin for the row
                    css: 'on-row', // CSS class for styling
                    responsive: 'a1', // Responsive design identifier
                    cols: [
                        // Define columns within the row
                        {
                            // Dropdown for selecting a country
                            view: 'richselect', // Type of input
                            name: 'CountrySelect', // Name of the input field
                            id: 'CountrySelect', // Unique identifier for the dropdown
                            minWidth: 200, // Minimum width for the column
                            localId: 'regions:combo', // Local identifier for the component
                            label: "<span class='icon-language'></span>" + _('País'), // Label with icon
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: {
                                // Options for the dropdown
                                body: {
                                    template: function (obj) {
                                        return (
                                            "<i class='flags " + obj.icon + "'></i>" + obj.value // Template for displaying country options
                                        );
                                    },
                                    data: countries, // Data source for the dropdown options
                                },
                            },
                            hidden: devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'), // Visibility based on permissions
                            css: 'prm-filterByCountry', // CSS class for styling
                        },
                        {
                            // Read-only dropdown for selecting a country
                            view: 'richselect', // Type of input
                            name: 'CountrySelect2', // Name of the input field
                            minWidth: 200, // Minimum width for the column
                            localId: 'regions:combo', // Local identifier for the component
                            label: "<span class='icon-language'></span>" + _('País'), // Label with icon
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            value: profile.Settings.ReportCountry, // Default value from profile settings
                            readonly: true, // Make the dropdown read-only
                            options: {
                                // Options for the dropdown
                                body: {
                                    template: function (obj) {
                                        return (
                                            "<i class='flags " + obj.icon + "'></i>" + obj.value // Template for displaying country options
                                        );
                                    },
                                    data: countries, // Data source for the dropdown options
                                },
                            },
                            hidden: !devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'), // Visibility based on permissions
                            css: 'prm-filterByCountry', // CSS class for styling
                            css: 'country-hide', // Additional CSS class for styling
                        },
                        {
                            // Dropdown for selecting registration status
                            view: 'richselect', // Type of input
                            name: 'IsRegisterActivate', // Name of the input field
                            label: "<span class='icon-state'></span>" + _('Estado de Registro'), // Label with icon
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            value: '', // Default value
                            options: [
                                // Options for the dropdown
                                { id: '', value: _('Todos') }, // Option for all
                                { id: 'A', value: _('Activo') }, // Option for active
                                { id: 'I', value: _('Inactivo') }, // Option for inactive
                                { id: 'N', value: _('No Procesado') }, // Option for not processed
                                { id: 'R', value: _('Rechazado') }, // Option for rejected
                            ],
                        },
                        {
                            // Dropdown for selecting activation status
                            view: 'richselect', // Type of input
                            name: 'IsActivate', // Name of the input field
                            label: "<span class='icon-state'></span>" + _('Estado'), // Label with icon
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            value: '', // Default value
                            options: [
                                // Options for the dropdown
                                { id: '', value: _('Ambos') }, // Option for both
                                { id: 'A', value: _('Activo') }, // Option for active
                                { id: 'I', value: _('Inactivo') }, // Option for inactive
                            ],
                        },
                        {
                            // Dropdown for selecting user type
                            view: 'richselect', // Type of input
                            name: 'Type', // Name of the input field
                            label: _('Tipo'), // Label text
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [
                                // Options for the dropdown
                                { id: '', value: _('Todos') }, // Option for all
                                { id: '0', value: _('Agente') }, // Option for agent
                                { id: '1', value: _('Afiliador') }, // Option for affiliate
                            ],
                        },
                    ],
                },
                {
                    margin: 15, // Margin for the row
                    css: 'on-row', // CSS class for styling
                    responsive: 'a1', // Responsive design identifier
                    cols: [
                        // Define columns within the row
                        {
                            // Input field for IP address
                            view: 'text', // Type of input
                            name: 'Ip', // Name of the input field
                            minWidth: 200, // Minimum width for the column
                            label: "<span class='icon-ip'></span>" + _('Dirección IP'), // Label with icon
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Direccion IP '), // Placeholder text
                        },
                        {
                            // Dropdown for selecting region profile
                            view: 'combo', // Type of input
                            name: 'RegionName', // Name of the input field
                            minWidth: 200, // Minimum width for the column
                            label: _('Región Perfil'), // Label text
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            hidden: profile.PartnerSelected != 8, // Visibility based on profile selection
                            options: [
                                // Options for the dropdown
                                { id: 'CENTRO', value: _('CENTRO') }, // Option for CENTRO
                                { id: 'COSTA', value: _('COSTA') }, // Option for COSTA
                                { id: 'AUSTRO', value: _('AUSTRO') }, // Option for AUSTRO
                            ],
                        },
                        { id: 'grav', gravity: 2 }, // Gravity setting for layout
                    ],
                },
            ],
        };
        // Define the upper section configuration for the UI component
        const upper_section = {
            css: 'father-container', // CSS class for styling the container
            rows: [
                {
                    margin: 10, // Margin around the row
                    cols: [left], // Columns within the row, 'left' is presumably defined elsewhere
                },
            ],
        };

        // Define the buttons configuration for the UI component
        const buttons = {
            id: 'b2', // Unique identifier for the button group
            rows: [
                {
                    margin: 10, // Margin around the row of buttons
                    responsive: 'b2', // Responsive design identifier
                    cols: [
                        { id: 'grav2', gravity: 4 }, // Column with gravity for layout adjustment
                        {
                            hidden: !devConfig.getPermAction(
                                // Determine visibility based on permissions
                                'agentListAdd', // Permission key
                                'Add', // Action type
                            ),
                            css: 'prm-agentListAdd', // CSS class for styling the button
                            view: 'button', // Specifies that this column is a button
                            value: _('Añadir Agente'), // Button label, translated
                            type: 'form', // Type of button, indicating it submits a form
                            width: screen == 'small' ? 0 : 130, // Conditional width based on screen size
                            minWidth: 200, // Minimum width for the button
                            click: () => {
                                // Click event handler for the button
                                if (
                                    this.getUrl()[0].page !== // Check current page
                                    'AgentListManagement'
                                )
                                    this.show(
                                        'addAgentListManagement/agent.addInformationAgentList', // Show add agent form
                                    );
                                else this.show('/agent.addInformationAgentList'); // Show add agent form directly
                            },
                        },
                        {
                            view: 'button', // Specifies that this column is a button
                            value: _('Limpiar'), // Button label for clearing
                            width: screen == 'small' ? 0 : 100, // Conditional width based on screen size
                            minWidth: 200, // Minimum width for the button
                            click: () => {
                                // Click event handler for the button
                                this.getRoot().clear(); // Clear the form or data in the root component
                            },
                        },
                        {
                            view: 'button', // Specifies that this column is a button
                            value: _('Consultar'), // Button label for consulting
                            hotkey: 'enter', // Keyboard shortcut for the button
                            type: 'form', // Type of button, indicating it submits a form
                            width: screen == 'small' ? 0 : 100, // Conditional width based on screen size
                            minWidth: 200, // Minimum width for the button
                            click: () => {
                                // Click event handler for the button
                                const filterdata = this.getRoot().getValues(); // Retrieve values from the root component
                                console.log(filterdata); // Log the filter data for debugging
                                if (devConfig.getCountryReport()) {
                                    // Check if country report is enabled
                                    filterdata.CountrySelect = profile.Settings.ReportCountry; // Set country filter based on profile settings
                                }
                                delete filterdata['CountrySelect2']; // Remove unnecessary filter data
                                this.app.callEvent('agentList:filter', [
                                    // Trigger filter event with the collected data
                                    filterdata,
                                ]);
                                $$('b5').collapse(); // Collapse a UI element with ID 'b5'
                                $$('a5').scrollTo(100, 0); // Scroll to a specific position in a UI element with ID 'a5'
                            },
                        },
                    ],
                },
            ],
        };
        const buttons2 = {
            // Define the structure for buttons, including rows and columns
            rows: [
                {
                    // Padding for the row
                    padding: 10,
                    // Define the columns within the row
                    cols: [
                        {
                            // Determine visibility based on permissions for adding agents
                            hidden: !devConfig.getPermAction('agentListAdd', 'Add'),
                            // CSS class for styling the button
                            css: 'prm-agentListAdd',
                            // Type of view for the button
                            view: 'button',
                            // Text displayed on the button, translated to the current language
                            value: _('Añadir Agente'),
                            // Type of button, indicating it submits a form
                            type: 'form',
                            // Click event handler for the button
                            click: () => {
                                // Hide the filter popup when the button is clicked
                                $$('filterPopup').hide();
                                // Navigate to the appropriate page based on the current URL
                                if (this.getUrl()[0].page !== 'AgentListManagement')
                                    this.show('addAgentListManagement/agent.addInformationAgentList');
                                else this.show('/agent.addInformationAgentList');
                            },
                        },
                        {
                            // Button to clear the form
                            view: 'button',
                            // Text displayed on the button, translated to the current language
                            value: _('Limpiar'),
                            // Click event handler for the button
                            click: () => {
                                // Clear the root component's data
                                this.getRoot().clear();
                            },
                        },
                        {
                            // Button to consult/filter the agent list
                            view: 'button',
                            // Text displayed on the button, translated to the current language
                            value: _('Consultar'),
                            // Hotkey for triggering the button action
                            hotkey: 'enter',
                            // Type of button, indicating it submits a form
                            type: 'form',
                            // Click event handler for the button
                            click: () => {
                                // Retrieve values from the form
                                const filterdata = $$('myForm2').getValues();
                                // Check if country report is enabled and set the country filter
                                if (devConfig.getCountryReport()) {
                                    filterdata.CountrySelect = profile.Settings.ReportCountry;
                                }
                                // Remove unnecessary country selection field
                                delete filterdata['CountrySelect2'];
                                // Trigger the filter event with the collected filter data
                                this.app.callEvent('agentList:filter', [filterdata]);
                            },
                        },
                    ],
                },
            ],
        };
        /**
         * Configuration object for an accordion section.
         * @type {Object}
         * @property {string} type - The type of the accordion section, set to 'wide'.
         * @property {Array} rows - An array of row configurations within the accordion.
         */
        const accordion_section = {
            type: 'wide',
            rows: [
                {
                    multi: true, // Allows multiple items to be expanded at once
                    view: 'accordion', // Specifies the view type as accordion
                    css: 'betShop', // CSS class for styling the accordion
                    collapsed: false, // Indicates whether the accordion is initially collapsed
                    type: 'clean', // Type of the accordion, possibly for styling purposes
                    rows: [
                        {
                            header: _('Filtros'), // Header text for the accordion section, localized
                            id: 'b5', // Unique identifier for the section
                            body: {
                                padding: 10, // Padding around the body content
                                rows: [upper_section, buttons], // Rows to be displayed in the body
                            },
                        },
                    ],
                },
            ],
        };

        /**
         * Configuration object for a scrollable section.
         * @type {Object}
         * @property {string} view - The view type, set to 'scrollview'.
         * @property {string} scroll - Specifies the scroll direction, set to 'xy' for both axes.
         * @property {Object} body - Contains the body configuration for the scrollview.
         */
        const accordion_section3 = {
            view: 'scrollview',
            scroll: 'xy', // Enables scrolling in both horizontal and vertical directions
            body: {
                padding: 10, // Padding around the body content
                rows: [upper_section], // Rows to be displayed in the body of the scrollview
            },
        };
        // Create a Webix UI window for filtering options
        const filterPopup = webix.ui({
            // Define the view type as a window
            view: 'window',
            // Allow the window to be moved
            move: true,
            // Unique identifier for the window
            id: 'filterPopup',
            // Set the window to fullscreen mode
            fullscreen: true,
            // Define the header of the window
            head: {
                view: 'toolbar',
                cols: [
                    // Label for the filter section
                    { view: 'label', label: _('Filtros'), align: 'center' },
                    {
                        // Close icon to hide the window
                        view: 'icon',
                        icon: 'wxi-close',
                        // Click event to hide the window
                        click: function () {
                            this.getTopParentView().hide(); // Hide the top parent view (the window)
                        },
                    },
                ],
            },
            // Set the window to be modal (blocks interaction with other elements)
            modal: true,
            // Position the window in the center of the screen
            position: 'center',
            // Define the body of the window
            body: {
                view: 'form',
                // Unique identifier for the form
                id: 'myForm2',
                // Set the form type to clean (no default styling)
                type: 'clean',
                // Define the rows within the form
                rows: [accordion_section3, buttons2],
                // Define validation rules for the form fields
                rules: {
                    // Rule to ensure the 'fname' field is not empty
                    fname: webix.rules.isNotEmpty,
                },
            },
        });
        /**
         * Returns a configuration object for a Webix form.
         * @returns {Object} The form configuration object.
         */
        return {
            /**
             * Unique identifier for the form.
             * @type {string}
             */
            id: 'filter',

            /**
             * Type of the view, in this case, a form.
             * @type {string}
             */
            view: 'form',

            /**
             * Specifies the type of the form layout.
             * @type {string}
             */
            type: 'clean',

            /**
             * Array of rows to be included in the form.
             * @type {Array}
             */
            rows: [
                /**
                 * Conditional rendering of the accordion section based on screen dimensions.
                 * If the screen width and height are both greater than 500,
                 * the accordion_section is included; otherwise, a placeholder is used.
                 * @type {Object}
                 */
                screen.width > 500 && screen.height > 500 ? accordion_section : { height: 1, width: 0 },
            ],

            /**
             * Validation rules for the form fields.
             * @type {Object}
             */
            rules: {
                /**
                 * Rule to ensure the 'fname' field is not empty.
                 * @type {Function}
                 */
                fname: webix.rules.isNotEmpty,
            },
        }; // Placeholder for the configuration content of the view
    }
}

// FILE DOCUMENTED
